import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { type WeightUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnits, formatUnitsAbbr } from './units'

const weightFormats: UnitsFormat<WeightUnit> = {
  lb: {
    long: 'pounds',
    short: 'lb'
  },
  kg: {
    long: 'kilograms',
    short: 'kg'
  }
}

function isWeightUnit(unit?: string): unit is WeightUnit {
  return unit !== undefined && unit in weightFormats
}

export const formatWeightUnits = formatUnits(weightFormats)
export const formatWeightUnitsAbbr = formatUnitsAbbr(weightFormats)

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'lb',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const useWeightFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: weight,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (!isWeightUnit(units)) return invalidStr
      if (typeof weight !== 'string' && typeof weight !== 'number') return invalidStr

      // Coerce to number
      const weightNum = parseFloat(`${weight}`)
      if (isNaN(weightNum)) return invalidStr

      const weightStr = formatNumber(weightNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return weightStr

      const formattedUnits = longForm ? formatWeightUnits(units) : formatWeightUnitsAbbr(units)
      return [weightStr, formattedUnits].join(' ')
    },
    [formatNumber]
  )
}
